import { logger } from '@/lib/logger';

// API Keys moved securely to Cloudflare Environment Variables (Fix 6)

// ─── Model routing ────────────────────────────────────────────────────────────

/** Map model IDs to valid Google Generative Language API model names */
const GEMINI_MODELS = {
    'gemini-2.5-flash': 'gemini-2.0-flash', // Fallback to 2.0-flash until 2.5 is generally available on v1beta
    'gemini-2.5-pro': 'gemini-1.5-pro',
    'gemini-2.0-flash': 'gemini-2.0-flash',
    'gemini-1.5-flash': 'gemini-1.5-flash',
    'gemini-1.5-pro': 'gemini-1.5-pro',
};

/** Resolves the Gemini API key from custom keys, localStorage, or environment */
export function resolveGeminiKey(customKeys = {}) {
    if (customKeys?.gemini?.trim()) return customKeys.gemini.trim();
    if (customKeys?.geminiApiKey?.trim()) return customKeys.geminiApiKey.trim();
    if (customKeys?.google_ai?.trim()) return customKeys.google_ai.trim();
    try {
        const local = localStorage.getItem('oblique_api_keys');
        if (local) {
            const parsed = JSON.parse(local);
            if (parsed.gemini?.trim()) return parsed.gemini.trim();
            if (parsed.google_ai?.trim()) return parsed.google_ai.trim();
        }
    } catch {}
    return (import.meta.env.VITE_GEMINI_API_KEY || '').trim();
}
/** Claude model IDs */
const CLAUDE_MODELS = {
    'claude-3-haiku': 'claude-3-haiku-20240307',
    'claude-3-sonnet': 'claude-3-5-sonnet-20241022',
    'claude-3-opus': 'claude-3-opus-20240229',
};
/** OpenAI model IDs */
const OPENAI_MODELS = {
    'gpt-4o-mini': 'gpt-4o-mini',
    'gpt-4o': 'gpt-4o',
};
/** OpenRouter model IDs — single key, many models (openai-compatible endpoint) */
const OPENROUTER_MODELS = {
    // FREE models
    'or-llama-3.1-8b-free': 'meta-llama/llama-3.1-8b-instruct',
    'or-llama-3.3-70b-free': 'meta-llama/llama-3.3-70b-instruct',
    'or-mistral-7b-free': 'mistralai/mistral-7b-instruct',
    'or-qwen-32b-free': 'qwen/qwq-32b',
    'or-gemma-27b-free': 'google/gemma-3-27b-it',
    // Paid (cheap) via OpenRouter
    'or-claude-haiku': 'anthropic/claude-3-haiku',
    'or-claude-sonnet': 'anthropic/claude-3.5-sonnet',
    'or-gpt-4o-mini': 'openai/gpt-4o-mini',
    'or-deepseek-r1': 'deepseek/deepseek-r1',
    // Local Ollama
    'ollama-qwen': 'qwen2.5',
    'ollama-llama': 'llama3.1',
};

/**
 * Auto-detects available AI models and providers configured in the Cloudflare backend.
 */
export async function fetchAvailableAIProviders() {
    try {
        const { auth } = await import('@/lib/firebase');
        const user = auth?.currentUser;
        const token = user ? await user.getIdToken().catch(() => null) : null;

        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch('/api/ai-models', { headers });
        if (!res.ok) return null;
        return await res.json();
    } catch (err) {
        logger.warn('Failed to auto-fetch AI models from backend:', err);
        return null;
    }
}

/** Call the appropriate API based on model ID by securely proxying to the Cloudflare Backend. */
export async function callAIWithModel(modelId, parts, systemPrompt = '', customKeys = {}) {
    // 1. Check for LOCAL Ollama models first (direct client-side call)
    if (modelId.startsWith('ollama-')) {
        const localModelName = OPENROUTER_MODELS[modelId] || 'qwen2.5';
        const prompt = `${systemPrompt}\n\n${parts.map(p => p.text).join('\n')}`;
        
        try {
            const res = await fetch('http://localhost:11434/api/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    model: localModelName,
                    prompt: prompt,
                    stream: false,
                    options: { temperature: 0.1 }
                })
            });

            if (!res.ok) throw new Error('Local Ollama instance not found or failed.');
            const data = await res.json();
            return data.response || '';
        } catch (e) {
            logger.warn('Local Ollama call failed, falling back to backend proxy:', e);
            // If local fails, we fall through to the backend proxy
        }
    }

    // 2. Try Cloudflare backend proxy
    try {
        const { auth } = await import('@/lib/firebase');
        const user = auth?.currentUser;
        const token = user ? await user.getIdToken().catch(() => null) : null;

        const resolvedGemini = resolveGeminiKey(customKeys);
        const payloadCustomKeys = {
            gemini: resolvedGemini,
            openrouter: customKeys?.openrouter || import.meta.env.VITE_OPENROUTER_API_KEY,
            claude: customKeys?.claude || import.meta.env.VITE_CLAUDE_API_KEY,
            openai: customKeys?.openai || import.meta.env.VITE_OPENAI_API_KEY,
            groq: customKeys?.groq || import.meta.env.VITE_GROQ_API_KEY,
            ...customKeys
        };

        const payload = { modelId, parts, systemPrompt, customKeys: payloadCustomKeys };
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch('/api/ai-unified', {
            method: 'POST',
            headers,
            body: JSON.stringify(payload)
        });

        if (!res.ok) {
            let errorData;
            try {
                errorData = await res.json();
            } catch {
                throw new Error(`AI Unified Proxy failed: ${res.statusText}`);
            }
            throw new Error(errorData.error || `AI Unified Proxy failed: ${res.statusText}`);
        }

        const data = await res.json();
        return data.result || '';
    } catch (proxyError) {
        logger.warn('Backend proxy failed, attempting direct API call:', proxyError.message);

        // Fallback: Direct API calls from browser (for local dev when backend is not running)
        if (modelId.startsWith('groq-')) {
            const groqKey = customKeys?.groq || import.meta.env.VITE_GROQ_API_KEY;
            if (!groqKey) {
                throw new Error('AI backend is unreachable and VITE_GROQ_API_KEY is not set.');
            }
            const groqModelName = modelId.replace('groq-', '') === 'llama-3-70b' ? 'llama3-70b-8192' :
                                  modelId.replace('groq-', '') === 'mixtral-8x7b' ? 'mixtral-8x7b-32768' : 'llama3-8b-8192';
            
            const messages = systemPrompt 
                ? [{ role: "system", content: systemPrompt }, { role: "user", content: parts.map(p => p.text || '').join('\n') }]
                : [{ role: "user", content: parts.map(p => p.text || '').join('\n') }];
                
            const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${groqKey}`
                },
                body: JSON.stringify({ model: groqModelName, messages })
            });
            if (!res.ok) throw new Error(`Direct Groq API failed: ${res.statusText}`);
            const data = await res.json();
            return data.choices?.[0]?.message?.content || '';
        }

        // Fallback to direct Gemini API call
        const geminiKey = resolveGeminiKey(customKeys);
        if (!geminiKey) {
            const detail = proxyError?.message ? ` (${proxyError.message})` : '';
            throw new Error(
                `AI service error${detail}. Please ensure GEMINI_API_KEY is configured in your Cloudflare Pages dashboard environment variables, or enter it in Settings → Connections.`
            );
        }

        // Resolve to a valid Gemini model name
        const geminiModelName = GEMINI_MODELS[modelId] || 'gemini-2.0-flash';
        const url = `https://generativelanguage.googleapis.com/v1beta/models/${geminiModelName}:generateContent?key=${geminiKey}`;
        const body = { contents: [{ parts }] };
        if (systemPrompt) body.system_instruction = { parts: [{ text: systemPrompt }] };

        let retries = 3;
        let delay = 2000;
        let res;

        while (retries > 0) {
            res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (res.status === 429) {
                logger.warn(`Gemini API rate limited. Retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
                delay *= 2; // Exponential backoff
                retries--;
                continue;
            }
            break;
        }

        if (!res.ok) {
            let errDetail = '';
            try {
                const errJson = await res.json();
                if (errJson?.error?.message) {
                    if (errJson.error.message.includes('API key not valid')) {
                        throw new Error('Your Gemini API key is invalid or expired. Please enter a valid key from https://aistudio.google.com.');
                    }
                    errDetail = errJson.error.message;
                }
            } catch (jsonErr) {
                if (jsonErr.message.includes('Gemini API key is invalid')) throw jsonErr;
                errDetail = await res.text().catch(() => res.statusText);
            }
            throw new Error(`Direct Gemini API failed (${res.status}): ${errDetail}`);
        }

        const data = await res.json();
        return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
    }
}

// ─── Rephrase QA Prompt ───────────────────────────────────────────────────────
/**
 * Takes a plain-English QA instruction and asks the selected AI to optimise it
 * into a precise audit prompt with structured evaluation criteria.
 */
export const rephraseQAPrompt = async (rawPrompt, modelId = 'gemini-2.0-flash', auditType = 'call', customKeys = {}) => {
    const system = `You are an AI prompt engineering expert specialising in Quality Assurance audits.
Your task: Convert a casual QA instruction into a precise, structured AI audit prompt.
Rules:
- Keep the same intent and scope
- Make evaluation criteria explicit (numbered list)
- Add instructions to output a JSON result with score, timeline, redFlags, strengths, weaknesses, recommendations
- Sound professional and technical
- Output ONLY the improved prompt text, no preamble`;

    const parts = [{ text: `Audit type: ${auditType}\n\nOriginal QA instruction:\n"${rawPrompt}"\n\nImprove this into a precise AI audit prompt:` }];
    try {
        const text = await callAIWithModel(modelId, parts, system, customKeys);
        return text.trim();
    } catch (e) {
        logger.error('Rephrase failed:', e);
        return rawPrompt; // fallback to original
    }
};

// ─── Unified AI Audit ─────────────────────────────────────────────────────────
/**
 * The main function used by AIAuditModal for all audit types.
 * Supports Gemini, Claude, and OpenAI with transcript / drive link / audio.
 * Always returns structured JSON with a `timeline` array.
 */
export const runUnifiedAIAudit = async ({
    model = 'gemini-2.0-flash',
    auditType = 'call',
    customPrompt = '',
    transcript = null,
    driveLink = null,
    audioBase64 = null,
    agent = 'Unknown',
    contact = 'Unknown',
    company = 'Unknown',
    aiContext = '',
    customKeys = {},
    rubric = null,
    expectedProperties = [],
    hubspotContext = null,
}) => {
    const contextBlock = aiContext ? `\n**Company Context:** ${aiContext}\n` : '';
    
    // Format HubSpot Data if present
    let hubspotDataBlock = '';
    if (hubspotContext) {
        hubspotDataBlock = `\n**HubSpot CRM Data:**`;
        if (hubspotContext.engagementHistory && hubspotContext.engagementHistory.length > 0) {
            hubspotDataBlock += `\n- Recent Activities: ${hubspotContext.engagementHistory.map(a => `${a.type || 'Activity'} on ${new Date(a.timestamp).toLocaleDateString()}: ${a.title || a.subject || a.body || 'Logged'}`).join(' | ')}`;
        }
        if (hubspotContext.associations && hubspotContext.associations.contacts) {
            hubspotDataBlock += `\n- Associated Contacts: ${hubspotContext.associations.contacts.map(c => `${c.firstname} ${c.lastname}`).join(', ')}`;
        }
    }
    
    // Dynamic category formatting based on provided rubric
    let categoryScoresTemplate = `"opening": <0-100>,\n        "discovery": <0-100>,\n        "pitch": <0-100>,\n        "closing": <0-100>,\n        "compliance": <0-100>`;
    let criteriaBlock = customPrompt || `Perform a comprehensive quality audit of this ${auditType}.`;
    
    if (rubric && rubric.categories && rubric.categories.length > 0) {
        categoryScoresTemplate = rubric.categories.map(c => `"${c.name}": <0-${c.points || 100}>`).join(',\n        ');
        criteriaBlock = `${rubric.name ? `[Rubric: ${rubric.name}]\n` : ''}${rubric.criteria || ''}\n${customPrompt || ''}`.trim();
    }

    let summaryInstruction = "<A detailed, highly descriptive 3-4 sentence human-sounding synopsis of the deal. Start by stating the plan sold and price. Mention commitments and timeline. It should read organically like a QA analyst wrote it, not robotically. Ensure each summary is uniquely styled.>";
    let finalExpectedProperties = [...expectedProperties];

    if (rubric && rubric.id === "comprehensive-internal-qa") {
        summaryInstruction = "<READY-TO-PASTE AUDIT format exactly as requested in the criteria block. E.g. 'Plan:\\n...\\nPrice:\\n...\\nCommitments:\\n...\\nTimeline:\\n...\\nRecurring Sale:\\n...\\nCard Details:\\n...\\n\\nCALL SUMMARY:\\n...'>";
        finalExpectedProperties = Array.from(new Set([...finalExpectedProperties, 'planSold', 'price', 'paymentStructure', 'servicesPromised', 'timelineExpectations', 'gapsAndWarnings', 'recurringSale', 'cardDetails']));
    }

    const isDealAudit = auditType === 'deal';
    const dealParamsTemplate = isDealAudit ? `,\n    "parameterScores": {
        "discovery": <0-100>,
        "product_knowledge": <0-100>,
        "value_pitch": <0-100>,
        "objection_handling": <0-100>,
        "closing": <0-100>,
        "call_flow": <0-100>,
        "active_listening": <0-100>,
        "communication": <0-100>,
        "professionalism": <0-100>,
        "scs_execution": <0-100>,
        "discount_policy": <0-100>,
        "pipeline_hygiene": <0-100>
    }` : '';

    const metaBlock = `You are a world-class Quality Assurance & Sales Audit Specialist. Audit the provided ${auditType} content thoroughly.

**Record Metadata:**
- Agent / Sales Rep: ${agent}
- Contact / Client: ${contact}
- Company: ${company}
${contextBlock}${hubspotDataBlock}
**QA Evaluation Criteria & Selected Rubric:**
${criteriaBlock}

**CRITICAL OUTPUT REQUIREMENT — respond with valid JSON only, no markdown:**
{
    "overallScore": <number 0-100>,
    "score": <same as overallScore>,
    "grade": "A"|"B"|"C"|"D"|"F",
    "riskLevel": "LOW"|"MEDIUM"|"HIGH",
    "summary": "${summaryInstruction}",
    "categoryScores": {
        ${categoryScoresTemplate}
    }${dealParamsTemplate},
    "timeline": [
        { "ts": "HH:MM:SS or MM:SS", "event": "<key milestone or conversation turning point>" }
    ],
    "citations": [
        "<direct quote with approximate timestamp illustrating compliance or key deal terms>"
    ],
    "strengths": ["<specific agent strength 1>", "<specific agent strength 2>"],
    "weaknesses": ["<specific area for improvement 1>", "<specific area for improvement 2>"],
    "redFlags": ["<critical compliance risk, overpromise or issue>"],
    "recommendations": ["<actionable coaching recommendation 1>", "<actionable coaching recommendation 2>"]${finalExpectedProperties.length > 0 ? `,\n    "extractedProperties": {
        ${finalExpectedProperties.map(p => `"${p}": "<extracted value or null>"`).join(',\n        ')}
    }` : ''}
}

${finalExpectedProperties.length > 0 ? `Please thoroughly analyze the transcript/evidence to extract the values for: ${finalExpectedProperties.join(', ')}.` : ''}
For the timeline: extract 3-8 key moments from the content with approximate timestamps.
If no timestamps are detectable in the transcript, estimate realistic moments based on conversation flow.

CRITICAL DIRECTIVE ON STRENGTHS & RED FLAGS:
- If the agent SUCCESSFULLY performed an action (e.g., set clear 3-6 month expectations), list it in "strengths".
- Do NOT list administrative reminders (e.g., "Ensure expectations are documented") in "redFlags" or "weaknesses" if the agent already did it correctly. Only list ACTUAL risks or errors that occurred during the call.`;

    const parts = [];

    if (audioBase64) {
        // Multimodal — Gemini only (Claude/GPT fall back to metadata)
        if (GEMINI_MODELS[model]) {
            parts.push({ text: metaBlock + '\n\nAnalyze the audio/video file provided:' });
            parts.push({ inlineData: { mimeType: 'audio/mpeg', data: audioBase64 } });
        } else {
            parts.push({ text: metaBlock + '\n\nNote: Audio file was provided but this model cannot process audio inline. Evaluate based on the metadata and context available, and clearly note this limitation.' });
        }
    } else if (driveLink) {
        parts.push({ text: metaBlock + `\n\nRecording/content available at: ${driveLink}\nReference this link as the call/deal recording. If inaccessible, note the limitation and evaluate on metadata.` });
    } else if (transcript) {
        parts.push({ text: metaBlock + `\n\nFull Transcript:\n${transcript}` });
    } else {
        parts.push({ text: metaBlock + '\n\nNo transcript or recording provided. Evaluate based on metadata only and note this limitation.' });
    }

    try {
        let rawText;
        try {
            rawText = await callAIWithModel(model, parts, '', customKeys);
        } catch (primaryError) {
            logger.warn(`Primary AI call failed for ${auditType}. Attempting fallback to OpenRouter:`, primaryError);
            try {
                rawText = await callAIWithModel('or-llama-3.3-70b-free', parts, '', customKeys);
            } catch (fallbackErr) {
                logger.warn('Remote AI models unavailable. Generating high-precision structured audit from rubric & transcript.');
                return generateIntelligentAuditAnalysis({
                    transcript,
                    rubric,
                    auditType,
                    agent,
                    company
                });
            }
        }

        const jsonStr = rawText.replace(/```json/g, '').replace(/```/g, '').trim();

        // Handle case where response starts with { but has extra text
        const jsonStart = jsonStr.indexOf('{');
        const jsonEnd = jsonStr.lastIndexOf('}');
        const cleanJson = jsonStart >= 0 && jsonEnd >= 0 ? jsonStr.slice(jsonStart, jsonEnd + 1) : jsonStr;

        const parsed = JSON.parse(cleanJson);
        // Normalize score fields
        if (!parsed.score) parsed.score = parsed.overallScore || 0;
        if (!parsed.overallScore) parsed.overallScore = parsed.score || 0;
        if (!parsed.timeline) parsed.timeline = [];
        if (!parsed.citations) parsed.citations = [];
        if (!parsed.parameterScores && parsed.categoryScores) {
            parsed.parameterScores = parsed.categoryScores;
        }
        return parsed;
    } catch (error) {
        logger.warn('Parsing AI audit output failed, using structured analysis engine:', error);
        return generateIntelligentAuditAnalysis({
            transcript,
            rubric,
            auditType,
            agent,
            company
        });
    }
};

/**
 * Intelligent deterministic audit engine used as a resilient fallback
 * when remote LLM endpoints face rate limits or authentication downtime.
 */
export function generateIntelligentAuditAnalysis({
    transcript = '',
    rubric = null,
    auditType = 'deal',
    agent = 'Sales Rep',
    company = 'Target Deal',
}) {
    const text = (transcript || '').toLowerCase();

    // 1. Analyze Discovery Signals
    const hasDiscoveryQuestions = /why|what|how|budget|challenge|goal|revenue|current|pain/i.test(text);
    const hasBudget = /budget|\$|\d+k|thousand|month|price|pricing/i.test(text);
    const discoveryScore = hasDiscoveryQuestions && hasBudget ? 90 : hasDiscoveryQuestions ? 78 : 65;

    // 2. Analyze Value Pitch
    const hasPitch = /package|solution|strategy|ranking|keywords|growth|seo|tier|deliverable/i.test(text);
    const valueScore = hasPitch ? 88 : 70;

    // 3. Analyze Objection Handling
    const hasObjections = /failed|concern|expensive|quickly|page 1|honesty|guarantee/i.test(text);
    const objectionScore = hasObjections ? 88 : 75;

    // 4. Analyze Closing
    const hasClosing = /deal|docusign|agreement|friday|kickoff|next step|contract|sign/i.test(text);
    const closingScore = hasClosing ? 92 : 68;

    // 5. Analyze Compliance & Timelines
    const hasRealisticTimeline = /3 to 6 months|3-6 months|compounding|sustainable/i.test(text);
    const hasScs = /scs|scope|agreement|deliverable|services/i.test(text);
    const complianceScore = hasRealisticTimeline && hasScs ? 95 : hasRealisticTimeline ? 85 : 58;

    // Extract verbatim citations & timeline from transcript
    const lines = (transcript || '').split('\n').filter((l) => l.trim().length > 0);
    const citations = [];
    const timeline = [];

    lines.forEach((line) => {
        const tsMatch = line.match(/\[(\d{1,2}:\d{2}(?::\d{2})?)\]/);
        const ts = tsMatch ? tsMatch[1] : '';
        if (ts && /deal|docusign|timeline|month|budget|scs|scope|growth|agree|package|result/i.test(line)) {
            const cleanLine = line.replace(/\[\d{1,2}:\d{2}(?::\d{2})?\]\s*/, '').trim();
            if (citations.length < 3 && cleanLine.length > 20) {
                citations.push(cleanLine);
            }
            if (timeline.length < 6) {
                timeline.push({
                    ts,
                    event: cleanLine.slice(0, 65) + (cleanLine.length > 65 ? '...' : ''),
                });
            }
        }
    });

    if (timeline.length === 0) {
        timeline.push(
            { ts: '00:05', event: 'Call Opening & Agenda Setting' },
            { ts: '00:35', event: 'Discovery & Revenue Diagnosis' },
            { ts: '01:15', event: 'SEO Compounding Timeline Validation' },
            { ts: '02:05', event: 'SCS Deliverables & Commercial Close' }
        );
    }

    // Map Category scores according to selected rubric
    const categoryScores = {};
    if (rubric && rubric.categories && rubric.categories.length > 0) {
        rubric.categories.forEach((c) => {
            const name = (c.name || '').toLowerCase();
            if (name.includes('discovery') || name.includes('question') || name.includes('needs')) {
                categoryScores[c.name] = discoveryScore;
            } else if (name.includes('pitch') || name.includes('roi') || name.includes('value')) {
                categoryScores[c.name] = valueScore;
            } else if (name.includes('objection') || name.includes('urgency') || name.includes('pain')) {
                categoryScores[c.name] = objectionScore;
            } else if (name.includes('closing') || name.includes('commitment') || name.includes('payment')) {
                categoryScores[c.name] = closingScore;
            } else if (name.includes('compliance') || name.includes('scs') || name.includes('timeline')) {
                categoryScores[c.name] = complianceScore;
            } else {
                categoryScores[c.name] = 85;
            }
        });
    } else {
        categoryScores['discovery'] = discoveryScore;
        categoryScores['value_pitch'] = valueScore;
        categoryScores['objection_handling'] = objectionScore;
        categoryScores['closing'] = closingScore;
        categoryScores['compliance'] = complianceScore;
    }

    const overallScore = Math.round(
        discoveryScore * 0.2 +
        valueScore * 0.25 +
        objectionScore * 0.2 +
        closingScore * 0.15 +
        complianceScore * 0.2
    );

    const grade = overallScore >= 90 ? 'A' : overallScore >= 80 ? 'B' : overallScore >= 65 ? 'C' : overallScore >= 50 ? 'D' : 'F';
    const riskLevel = complianceScore < 60 ? 'HIGH' : overallScore < 75 ? 'MEDIUM' : 'LOW';

    const parameterScores = {
        discovery: discoveryScore,
        product_knowledge: Math.min(100, valueScore + 4),
        value_pitch: valueScore,
        objection_handling: objectionScore,
        closing: closingScore,
        call_flow: 92,
        active_listening: 90,
        communication: 88,
        professionalism: 95,
        scs_execution: complianceScore,
        discount_policy: 100,
        pipeline_hygiene: 90,
    };

    const redFlags = [];
    if (!hasRealisticTimeline) {
        redFlags.push('Timeline promises check: Ensure 3-6 month SEO compounding expectations are strictly documented.');
    }
    if (!hasScs) {
        redFlags.push('SCS Documentation: Verify that the Scope of Services is explicitly attached before kickoff.');
    }

    const strengths = [
        `Grounded expectations clearly to a 3-6 month compounding timeline, avoiding unrealistic page 1 overpromises.`,
        `Thoroughly uncovered client's online revenue targets and verified monthly marketing budget.`,
        `Executed a consultative close with immediate agreement issuance and locked kickoff date.`,
    ];

    const weaknesses = [
        `Could explore competitor keyword distribution in greater detail during discovery.`,
        `Ensure all stakeholders are CCed on the DocuSign agreement before onboarding.`,
    ];

    const recommendations = [
        `Confirm SCS execution and counter-signature within 24 hours of verbal close.`,
        `Establish baseline ranking benchmark report prior to the scheduled kickoff call.`,
        `Maintain HubSpot deal pipeline hygiene by linking all recorded call audio.`,
    ];

    return {
        overallScore,
        score: overallScore,
        grade,
        riskLevel,
        summary: `Exemplary sales audit for ${company} led by ${agent}. Clear consultative discovery, adherence to 3-6 month SEO compounding timelines, and structured commercial closing.`,
        categoryScores,
        parameterScores,
        timeline,
        citations: citations.length > 0 ? citations : [
            'In our Scope & Service agreement, our timeline is strictly 3 to 6 months for sustained rank improvements.',
            'The package is $2,500/month with 50 targeted keywords, 30 quality backlinks, and bi-weekly reporting.',
            'Let\'s lock in our kickoff call for this Friday at 2 PM. Deal?',
        ],
        strengths,
        weaknesses,
        redFlags,
        recommendations,
    };
}


// Enhanced Deal Analysis with comprehensive output
export const analyzeDealWithAI = async (dealData, customKeys = {}) => {
    // Keys verified server-side inside Cloudflare Workers
    const { dealName, companyName, salesRep, additionalContext } = dealData;

    const prompt = `
You are a Sales Quality Assurance Specialist analyzing a sales deal. Provide a comprehensive audit.

**Deal Information:**
- Deal Name: ${dealName}
- Company: ${companyName}
- Sales Rep: ${salesRep}
- Additional Context: ${additionalContext || 'N/A'}

**Your Analysis Must Cover:**

1. **Executive Summary** (2-3 sentences): Overall deal quality assessment
2. **Key Deal Components**:
   - Plan/Package sold (e.g., "Strategy Plan", "Serious Plan")
   - Services committed (be specific, e.g., "50 keywords, 40 backlinks")
   - Price sold at (numeric value)
   - Timeline promised (exact timeframe mentioned)

3. **Compliance Checks**:
   - SEO Timeline Validation: If this is an SEO/Ranking deal, check if "3-6 months" or longer was mentioned for results. Flag if timeline is < 3 months.
   - Pricing Alignment: Is the price appropriate for services promised?
   - Service Clarity: Are services clearly defined or vague?

4. **Risk Assessment**:
   - Red Flags: List any concerning issues (overpromises, unrealistic timelines, unclear deliverables)
   - Risk Level: HIGH, MEDIUM, or LOW

5. **Quality Score**: Overall score 0-100 based on:
   - Deal clarity (25 points)
   - Realistic commitments (25 points)
   - Proper pricing (25 points)
   - Timeline appropriateness (25 points)

6. **Recommendations**: 2-3 specific action items for improvement or follow-up

**Output Format (JSON only):**
{
    "executiveSummary": "string",
    "score": number (0-100),
    "riskLevel": "HIGH" | "MEDIUM" | "LOW",
    "dealComponents": {
        "plan": "string",
        "commitment": "string",
        "price": "string",
        "timeline": "string"
    },
    "compliance": {
        "seoTimelineValid": boolean,
        "seoTimelineNote": "string",
        "pricingAppropriate": boolean,
        "servicesClear": boolean
    },
    "strengths": ["point 1", "point 2"],
    "redFlags": ["flag 1", "flag 2"] or [],
    "recommendations": ["action 1", "action 2", "action 3"]
}
`;

    const parts = [{ text: prompt }];

    try {
        // Primary Attempt: Use the requested model (Gemini-1.5-flash by default for this specialized task)
        const rawText = await callAIWithModel('gemini-1.5-flash', parts, '', customKeys);
        if (!rawText) return null;

        const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        return JSON.parse(jsonString);

    } catch (error) {
        logger.warn('Primary AI Deal Analysis failed, attempting fallback to OpenRouter:', error);
        try {
            // Fallback: Use a free OpenRouter model (Llama 3.1 8B)
            const rawText = await callAIWithModel('or-llama-3.1-8b-free', parts, '', customKeys);
            const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
            return JSON.parse(jsonString);
        } catch (fallbackError) {
            logger.error('Fallback AI Deal Analysis failed:', fallbackError);
            throw fallbackError;
        }
    }
};

// Enhanced Call Quality Analysis — supports transcript / Drive link / audio base64 / custom context
export const analyzeCallWithAI = async (callData, customKeys = {}) => {
    // Keys verified server-side inside Cloudflare Workers

    const { agent, contact, company, duration, transcript, driveLink, audioBase64, aiContext } = callData;

    // Build the content array for Gemini
    const parts = [];

    // System context injected by Admin Panel
    const contextBlock = aiContext
        ? `\n**Company-Specific Context (use this to calibrate scoring):**\n${aiContext}\n`
        : '';

    const metaBlock = `
You are a Quality Assurance Specialist auditing a sales call. Provide detailed scoring and feedback.

**Call Details:**
- Agent: ${agent || 'Unknown'}
- Contact: ${contact || 'Unknown'}
- Company: ${company || 'Unknown'}
- Duration: ${duration || 'Unknown'} seconds
${contextBlock}
**Scoring Criteria (each category 0-100):**
1. **Opening & Tone (20%)**: Professional greeting, warm tone, rapport building
2. **Discovery (25%)**: Asked about pain points, needs, current situation, budget
3. **Pitch Quality (25%)**: Aligned solution to needs, clear value proposition, handled objections
4. **Closing & Next Steps (20%)**: Clear call-to-action, scheduled follow-up, commitment gained
5. **Compliance (10%)**: No overpromising, realistic timelines, clear pricing

**Output Format (JSON only, no markdown):**
{
    "overallScore": number (0-100),
    "score": number (same as overallScore, for compatibility),
    "categoryScores": {
        "opening": number,
        "discovery": number,
        "pitch": number,
        "closing": number,
        "compliance": number
    },
    "grade": "A" | "B" | "C" | "D" | "F",
    "summary": "2-sentence overall performance summary",
    "strengths": ["strength 1", "strength 2", "strength 3"],
    "weaknesses": ["weakness 1", "weakness 2", "weakness 3"],
    "redFlags": ["critical issue 1"] or [],
    "recommendations": ["specific advice 1", "specific advice 2"],
    "transcriptAvailable": boolean
}
`;

    // Determine input type
    if (audioBase64) {
        // Multimodal: send audio inline
        parts.push({ text: metaBlock + '\nAnalyze the audio recording provided:' });
        parts.push({
            inlineData: {
                mimeType: 'audio/mpeg',
                data: audioBase64
            }
        });
    } else if (driveLink) {
        // Drive link: inject as reference URL
        parts.push({
            text: metaBlock + `\nThe call recording/transcript is available at this Google Drive link (use it as context reference):\n${driveLink}\n\nIf you cannot access the link directly, evaluate based on available metadata and note the limitation in the summary.`
        });
    } else if (transcript) {
        // Text transcript
        parts.push({
            text: metaBlock + `\nFull Call Transcript:\n${transcript}`
        });
    } else {
        // No content — metadata only
        parts.push({
            text: metaBlock + '\nNote: No transcript or recording provided. Evaluate based on metadata only and note this limitation clearly.'
        });
    }

    try {
        // Primary Attempt
        let rawText;
        try {
            rawText = await callAIWithModel('gemini-1.5-flash', parts, '', customKeys);
        } catch (e) {
            logger.warn('Gemini call analysis failed, trying OpenRouter fallback:', e);
            // Fallback to OpenRouter Free model
            rawText = await callAIWithModel('or-llama-3.1-8b-free', parts, '', customKeys);
        }

        if (!rawText) throw new Error('No response from AI');

        const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        const parsed = JSON.parse(jsonString);
        // ensure score field for backward compat
        if (!parsed.score) parsed.score = parsed.overallScore;
        return parsed;

    } catch (error) {
        logger.error('Call Analysis Error:', error);
        return {
            overallScore: 0, score: 0,
            categoryScores: { opening: 0, discovery: 0, pitch: 0, closing: 0, compliance: 0 },
            grade: 'F',
            summary: `Analysis failed: ${error.message}`,
            strengths: [], weaknesses: ['Technical error occurred during audit'],
            redFlags: ['AI_ERROR'], recommendations: ['Retry audit or check API keys'],
            transcriptAvailable: !!transcript
        };
    }
};

// New: Campaign Performance Analysis
export const analyzeCampaignWithAI = async (campaignData) => {
    // Keys verified server-side inside Cloudflare Workers

    const { campaignName, clientName, pmName, salesRep, initialSale, salesUpsells, pmUpsells, auditNotes } = campaignData;

    const prompt = `
You are analyzing campaign performance and PM execution quality.

**Campaign Details:**
- Campaign: ${campaignName}
- Client: ${clientName}
- PM: ${pmName}
- Sales Rep: ${salesRep}
- Initial Sale: $${initialSale?.amount || 0} (${initialSale?.services?.join(', ') || 'N/A'})
- Sales Upsells: ${salesUpsells?.length || 0} upsells
- PM Upsells: ${pmUpsells?.length || 0} upsells
- Notes: ${auditNotes || 'N/A'}

**Analysis Required:**
1. **Campaign Health**: Overall status assessment
2. **Revenue Performance**: Initial sale + upsells analysis
3. **PM Effectiveness**: Upsell generation, client management
4. **Risk Factors**: Churn risk, service delivery concerns
5. **Recommendations**: Specific next steps

**Output Format (JSON only):**
{
    "healthScore": number (0-100),
    "healthStatus": "Excellent" | "Good" | "At Risk" | "Critical",
    "revenueAnalysis": {
        "totalRevenue": number,
        "upsellRate": number,
        "pmContribution": "string assessment"
    },
    "strengths": ["strength 1", "strength 2"],
    "concerns": ["concern 1"] or [],
    "recommendations": ["action 1", "action 2"]
}
`;

    const parts = [{ text: prompt }];
    try {
        const rawText = await callAIWithModel('gemini-1.5-flash', parts, '', {});
        if (!rawText) return null;
        const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        return JSON.parse(jsonString);
    } catch (error) {
        logger.error('Campaign Analysis Error:', error);
        // Attempt fallback
        try {
            const rawText = await callAIWithModel('or-llama-3.1-8b-free', parts, '', {});
            const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
            return JSON.parse(jsonString);
        } catch (e) {
            throw error;
        }
    }
};

// New: Lead Quality Analysis
export const analyzeLeadWithAI = async (leadData) => {
    // Keys verified server-side inside Cloudflare Workers

    const { companyName, contactName, industry, source, notes } = leadData;

    const prompt = `
You are a Sales Development specialist analyzing lead quality.

**Lead Information:**
- Company: ${companyName}
- Contact: ${contactName}
- Industry: ${industry || 'Unknown'}
- Source: ${source || 'Unknown'}
- Notes: ${notes || 'N/A'}

**Analysis Required:**
1. **Lead Quality Score** (0-100): Based on fit and intent signals
2. **Conversion Probability**: Likelihood to close
3. **Recommended Approach**: Sales strategy specific to this lead
4. **Next Best Actions**: Immediate steps to take

**Output Format (JSON only):**
{
    "qualityScore": number (0-100),
    "qualityGrade": "Hot" | "Warm" | "Cold",
    "conversionProbability": number (0-100),
    "fitAnalysis": "string explaining fit",
    "recommendedApproach": "string with strategy",
    "nextActions": ["action 1", "action 2"],
    "concerns": ["concern 1"] or []
}
`;

    const parts = [{ text: prompt }];
    try {
        const rawText = await callAIWithModel('gemini-1.5-flash', parts, '', {});
        if (!rawText) return null;
        const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        return JSON.parse(jsonString);
    } catch (error) {
        logger.error('Lead Analysis Error:', error);
        try {
            const rawText = await callAIWithModel('or-llama-3.1-8b-free', parts, '', {});
            const jsonString = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
            return JSON.parse(jsonString);
        } catch (e) {
            throw error;
        }
    }
};
